// ✅ VisitorReportsPage.js
// TaskFlow ERP — Visitor Reports + Analytics (UI Only)
// Premium Gradient Style (matches B3-C Dashboard)
// Created: 2025-11-22

import React, { useState, useMemo } from "react";
import MultiSelectDropdown from "../components/MultiSelectDropdown"; // <-- SAME COMPONENT YOU APPROVED
import { useNavigate } from "react-router-dom";

function VisitorReportsPage() {
  const navigate = useNavigate();

  // SAMPLE REPORT DATA (UI Only)
  const reports = [
    {
      id: 1,
      visitor: "Rajesh Kumar",
      company: "XYZ Corp",
      host: "Manish",
      visitDate: "2025-11-20",
      checkIn: "10:15 AM",
      checkOut: "11:05 AM",
      status: "Checked-Out",
    },
    {
      id: 2,
      visitor: "Anita Sharma",
      company: "ABC Pvt Ltd",
      host: "Happy",
      visitDate: "2025-11-20",
      checkIn: "--",
      checkOut: "--",
      status: "Approved",
    },
    {
      id: 3,
      visitor: "Rohit Singh",
      company: "TechnoSoft",
      host: "Manish",
      visitDate: "2025-11-20",
      checkIn: "09:05 AM",
      checkOut: "--",
      status: "Checked-In",
    },
  ];

  const statusColors = {
    "Checked-In": "bg-info-subtle text-info",
    "Checked-Out": "bg-success-subtle text-success",
    Approved: "bg-primary-subtle text-primary",
    Rejected: "bg-danger-subtle text-danger",
  };

  // FILTER STATES
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [hostFilter, setHostFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 6;

  // FILTER LOGIC
  const filteredReports = useMemo(() => {
    return reports.filter((v) => {
      const matchesSearch =
        !search ||
        v.visitor.toLowerCase().includes(search.toLowerCase()) ||
        v.company.toLowerCase().includes(search.toLowerCase()) ||
        v.host.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter.length === 0 || statusFilter.includes(v.status);

      const matchesHost =
        hostFilter.length === 0 || hostFilter.includes(v.host);

      const matchesFrom = !fromDate || v.visitDate >= fromDate;
      const matchesTo = !toDate || v.visitDate <= toDate;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesHost &&
        matchesFrom &&
        matchesTo
      );
    });
  }, [search, statusFilter, hostFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / rowsPerPage));
  const paginated = filteredReports.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // KPI DATA (UI Only)
  const kpi = {
    total: reports.length,
    approved: reports.filter((r) => r.status === "Approved").length,
    in: reports.filter((r) => r.status === "Checked-In").length,
    out: reports.filter((r) => r.status === "Checked-Out").length,
    rejected: reports.filter((r) => r.status === "Rejected").length,
  };

  // KPI Gradient Cards
  const kpiCards = [
    {
      label: "Total Visits",
      value: kpi.total,
      gradient: "linear-gradient(135deg, #2e1065, #7c3aed, #a78bfa)",
    },
    {
      label: "Approved",
      value: kpi.approved,
      gradient: "linear-gradient(135deg, #064e3b, #059669, #34d399)",
    },
    {
      label: "Checked-In",
      value: kpi.in,
      gradient: "linear-gradient(135deg, #0f172a, #0369a1, #3b82f6)",
    },
    {
      label: "Checked-Out",
      value: kpi.out,
      gradient: "linear-gradient(135deg, #1e1b4b, #4c1d95, #7e22ce)",
    },
    {
      label: "Rejected",
      value: kpi.rejected,
      gradient: "linear-gradient(135deg, #450a0a, #b91c1c, #ef4444)",
    },
  ];

  return (
    <div className="container-fluid py-3">

      {/* HEADER */}
      <div
        className="p-4 mb-4 shadow-sm"
        style={{
          borderRadius: "20px",
          background:
            "linear-gradient(95deg,#4f46e5 0%,#6366f1 40%,#0ea5e9 100%)",
          color: "#fff",
        }}
      >
        <h3 className="fw-bold mb-1">Visitor Reports</h3>
        <small className="opacity-75">
          Analyze visits, check-ins, approvals and host patterns.
        </small>
      </div>

      {/* FILTERS */}
      <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "20px" }}>
        <div className="card-body">
          <div className="row g-3">

            <div className="col-md-3">
              <label className="form-label fw-semibold small">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                style={{ borderRadius: "10px" }}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold small">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                style={{ borderRadius: "10px" }}
              />
            </div>

            <div className="col-md-3">
              <MultiSelectDropdown
                label="Status"
                options={["Approved", "Checked-In", "Checked-Out", "Rejected"]}
                selectedValues={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Status"
              />
            </div>

            <div className="col-md-3">
              <MultiSelectDropdown
                label="Host"
                options={["Manish", "Happy", "IBM Support"]}
                selectedValues={hostFilter}
                onChange={setHostFilter}
                placeholder="All Hosts"
              />
            </div>

            <div className="col-md-9">
              <label className="form-label fw-semibold small">Search</label>
              <input
                className="form-control"
                placeholder="Search visitor / company / host"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setStatusFilter([]);
                  setHostFilter([]);
                  setSearch("");
                  setPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="row g-3 mb-4">
        {kpiCards.map((k) => (
          <div key={k.label} className="col-6 col-md-4 col-lg-2">
            <div
              className="text-white shadow-sm"
              style={{
                borderRadius: "20px",
                padding: "18px",
                background: k.gradient,
              }}
            >
              <div className="small opacity-90">{k.label}</div>
              <div className="h3 fw-bold">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* TABLE */}
      <div className="card shadow-sm border-0" style={{ borderRadius: "20px" }}>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr className="small text-uppercase text-muted">
                <th>#</th>
                <th>Visitor</th>
                <th>Company</th>
                <th>Host</th>
                <th>Date</th>
                <th>Check-In</th>
                <th>Check-Out</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {paginated.map((v, i) => (
                <tr key={v.id}>
                  <td>{(page - 1) * rowsPerPage + i + 1}</td>
                  <td>{v.visitor}</td>
                  <td className="small text-muted">{v.company}</td>
                  <td>{v.host}</td>
                  <td>{v.visitDate}</td>
                  <td>{v.checkIn}</td>
                  <td>{v.checkOut}</td>
                  <td>
                    <span className={`badge rounded-pill ${statusColors[v.status]}`}>
                      {v.status}
                    </span>
                  </td>
                </tr>
              ))}

              {paginated.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No matching records.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="d-flex justify-content-between align-items-center p-3">
          <span className="small text-muted">
            Page {page} of {totalPages}
          </span>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------- */}
      {/* 2️⃣ ANALYTICS WIDGETS (PLACEHOLDERS FOR NOW) */}
      {/* ---------------------------------------------- */}
      <h4 className="fw-bold mt-5 mb-3">Visitor Analytics</h4>

      <div className="row g-3">

        {/* Daily Trend */}
        <div className="col-lg-6">
          <div
            className="card shadow-sm"
            style={{ borderRadius: "20px", height: "280px" }}
          >
            <div className="card-body">
              <h6 className="fw-semibold mb-1">Daily Visit Trend</h6>
              <small className="text-muted">Chart placeholder</small>
              <div className="d-flex justify-content-center align-items-center h-75 text-muted">
                [ Line Chart Placeholder ]
              </div>
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="col-lg-6">
          <div
            className="card shadow-sm"
            style={{ borderRadius: "20px", height: "280px" }}
          >
            <div className="card-body">
              <h6 className="fw-semibold mb-1">Status Distribution</h6>
              <small className="text-muted">Pie chart placeholder</small>
              <div className="d-flex justify-content-center align-items-center h-75 text-muted">
                [ Pie Chart Placeholder ]
              </div>
            </div>
          </div>
        </div>

        {/* Host-wise Visits */}
        <div className="col-lg-6">
          <div
            className="card shadow-sm"
            style={{ borderRadius: "20px", height: "280px" }}
          >
            <div className="card-body">
              <h6 className="fw-semibold mb-1">Host-wise Visit Count</h6>
              <small className="text-muted">Bar chart placeholder</small>
              <div className="d-flex justify-content-center align-items-center h-75 text-muted">
                [ Horizontal Bar Chart Placeholder ]
              </div>
            </div>
          </div>
        </div>

        {/* Repeat Visitors */}
        <div className="col-lg-6">
          <div
            className="card shadow-sm"
            style={{ borderRadius: "20px", height: "280px" }}
          >
            <div className="card-body">
              <h6 className="fw-semibold mb-1">Repeat Visitor Summary</h6>
              <div className="h-75 d-flex flex-column justify-content-center align-items-center gap-3">
                <div className="badge bg-primary-subtle text-primary px-3 py-2">
                  Repeat Visitors: 12
                </div>
                <div className="badge bg-warning-subtle text-warning px-3 py-2">
                  Frequent Visitors: 3
                </div>
                <div className="badge bg-secondary-subtle text-secondary px-3 py-2">
                  VIP Repeat: 1
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}

export default VisitorReportsPage;
